import { useAccountingCompany } from '../components/HoldingAccounting';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function FinancialProjects() {
  const companyId = useAccountingCompany();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const base = `${API}/companies/${companyId}/finance`;

  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [tab, setTab] = useState('projects');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [filters, setFilters] = useState({ category_id: '', status: '', search: '' });
  const [form, setForm] = useState({ name: '', category_id: '', project_type_id: '', description: '', start_date: '', end_date: '', expected_revenue: '', expected_expenses: '', responsible_user_id: '' });
  const [catForm, setCatForm] = useState({ name: '', description: '', parent_id: '' });

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      fetch(`${base}/categories`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
      fetch(`${base}/projects?${new URLSearchParams(filters)}`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : { data: [] }),
      fetch(`${base}/settings/project-types`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
    ]).then(([cats, projs, types]) => {
      setCategories(Array.isArray(cats) ? cats : []);
      setProjects(projs.data ? projs.data : Array.isArray(projs) ? projs : []);
      setProjectTypes(Array.isArray(types) ? types : []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { if (companyId) fetchAll(); }, [companyId]);

  const saveProject = async () => {
    const url = editing ? `${base}/projects/${editing.id}` : `${base}/projects`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) { setShowForm(false); setEditing(null); setForm({ name: '', category_id: '', project_type_id: '', description: '', start_date: '', end_date: '', expected_revenue: '', expected_expenses: '', responsible_user_id: '' }); fetchAll(); }
  };

  const saveCat = async () => {
    const url = editingCat ? `${base}/categories/${editingCat.id}` : `${base}/categories`;
    const method = editingCat ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(catForm) });
    if (res.ok) { setShowCatForm(false); setEditingCat(null); setCatForm({ name: '', description: '', parent_id: '' }); fetchAll(); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await fetch(`${base}/categories/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    fetchAll();
  };

  const archiveProject = async (id) => {
    await fetch(`${base}/projects/${id}/archive`, { method: 'POST', headers: getAuthHeaders() });
    fetchAll();
  };

  const restoreProject = async (id) => {
    await fetch(`${base}/projects/${id}/restore`, { method: 'POST', headers: getAuthHeaders() });
    fetchAll();
  };

  const editProject = (p) => { setEditing(p); setForm({ name: p.name, category_id: p.category_id || '', project_type_id: p.project_type_id || '', description: p.description || '', start_date: p.start_date || '', end_date: p.end_date || '', expected_revenue: p.expected_revenue || '', expected_expenses: p.expected_expenses || '', responsible_user_id: p.responsible_user_id || '' }); setShowForm(true); };
  const editCat = (c) => { setEditingCat(c); setCatForm({ name: c.name, description: c.description || '', parent_id: c.parent_id || '' }); setShowCatForm(true); };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const btnStyle = { padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 };
  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem', boxSizing: 'border-box' };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
  const statusBadge = (s) => {
    const colors = { active: '#22c55e', archived: '#94a3b8', completed: '#3b82f6', on_hold: '#f59e0b' };
    return { display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: (colors[s] || '#94a3b8') + '20', color: colors[s] || '#94a3b8' };
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>📂 Projects & Structure</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setTab('projects')} style={{ ...btnStyle, background: tab === 'projects' ? '#3b82f6' : '#f1f5f9', color: tab === 'projects' ? '#fff' : '#374151' }}>Projects</button>
          <button onClick={() => setTab('categories')} style={{ ...btnStyle, background: tab === 'categories' ? '#3b82f6' : '#f1f5f9', color: tab === 'categories' ? '#fff' : '#374151' }}>Categories</button>
        </div>
      </div>

      {tab === 'projects' && (
        <>
          {/* Filters */}
          <div style={{ ...sectionStyle, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input placeholder="Search..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} style={{ ...inputStyle, maxWidth: 220 }} />
            <select value={filters.category_id} onChange={e => setFilters({ ...filters, category_id: e.target.value })} style={{ ...inputStyle, maxWidth: 180 }}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} style={{ ...inputStyle, maxWidth: 160 }}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
            </select>
            <button onClick={fetchAll} style={{ ...btnStyle, background: '#e2e8f0' }}>🔍 Filter</button>
            <div style={{ flex: 1 }} />
            <button onClick={() => { setEditing(null); setForm({ name: '', category_id: '', project_type_id: '', description: '', start_date: '', end_date: '', expected_revenue: '', expected_expenses: '', responsible_user_id: '' }); setShowForm(true); }} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>+ New Project</button>
          </div>

          {/* Projects Table */}
          <div style={sectionStyle}>
            {initialLoading ? <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Code</th><th style={thStyle}>Name</th><th style={thStyle}>Category</th><th style={thStyle}>Type</th>
                      <th style={thStyle}>Status</th><th style={{ ...thStyle, textAlign: 'right' }}>Expected Rev.</th><th style={{ ...thStyle, textAlign: 'right' }}>Expected Exp.</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.length === 0 ? (
                      <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No projects found</td></tr>
                    ) : projects.map(p => (
                      <tr key={p.id}>
                        <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6366f1' }}>{p.project_code}</span></td>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{p.name}</td>
                        <td style={tdStyle}>{p.category?.name || '-'}</td>
                        <td style={tdStyle}>{p.project_type?.name || '-'}</td>
                        <td style={tdStyle}><span style={statusBadge(p.status)}>{p.status}</span></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.expected_revenue)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.expected_expenses)}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => editProject(p)} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>✏️</button>
                            {p.status === 'active' ? (
                              <button onClick={() => archiveProject(p.id)} style={{ ...btnStyle, background: '#fef3c7', padding: '4px 10px', fontSize: '0.8rem' }}>📦</button>
                            ) : p.status === 'archived' ? (
                              <button onClick={() => restoreProject(p.id)} style={{ ...btnStyle, background: '#d1fae5', padding: '4px 10px', fontSize: '0.8rem' }}>♻️</button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Project Form Modal */}
          {showForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '90%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
                <h3 style={{ margin: '0 0 20px', fontSize: '1.1rem' }}>{editing ? 'Edit Project' : 'New Project'}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div style={{ gridColumn: '1 / -1' }}><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Category</label><select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} style={inputStyle}><option value="">Select</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Type</label><select value={form.project_type_id} onChange={e => setForm({ ...form, project_type_id: e.target.value })} style={inputStyle}><option value="">Select</option>{projectTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Start Date</label><input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>End Date</label><input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Expected Revenue</label><input type="number" step="0.01" value={form.expected_revenue} onChange={e => setForm({ ...form, expected_revenue: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Expected Expenses</label><input type="number" step="0.01" value={form.expected_expenses} onChange={e => setForm({ ...form, expected_expenses: e.target.value })} style={inputStyle} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Description</label><textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                  <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ ...btnStyle, background: '#e2e8f0' }}>Cancel</button>
                  <button onClick={saveProject} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Save</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'categories' && (
        <>
          <div style={{ ...sectionStyle, display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => { setEditingCat(null); setCatForm({ name: '', description: '', parent_id: '' }); setShowCatForm(true); }} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>+ New Category</button>
          </div>
          <div style={sectionStyle}>
            {initialLoading ? <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={thStyle}>Name</th><th style={thStyle}>Description</th><th style={thStyle}>Parent</th><th style={thStyle}>Actions</th></tr></thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No categories found</td></tr>
                    ) : categories.map(c => (
                      <tr key={c.id}>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{c.name}</td>
                        <td style={tdStyle}>{c.description || '-'}</td>
                        <td style={tdStyle}>{categories.find(p => p.id === c.parent_id)?.name || '-'}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => editCat(c)} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>✏️</button>
                            <button onClick={() => deleteCat(c.id)} style={{ ...btnStyle, background: '#fee2e2', padding: '4px 10px', fontSize: '0.8rem' }}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {showCatForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '90%', maxWidth: 480 }}>
                <h3 style={{ margin: '0 0 20px' }}>{editingCat ? 'Edit Category' : 'New Category'}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name *</label><input value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Parent Category</label><select value={catForm.parent_id} onChange={e => setCatForm({ ...catForm, parent_id: e.target.value })} style={inputStyle}><option value="">None (Top Level)</option>{categories.filter(c => c.id !== editingCat?.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Description</label><textarea rows={3} value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                  <button onClick={() => { setShowCatForm(false); setEditingCat(null); }} style={{ ...btnStyle, background: '#e2e8f0' }}>Cancel</button>
                  <button onClick={saveCat} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Save</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
